<template>
  <main class="recipes-list-view">
    <section class="recipes-hero">
      <div class="recipes-hero__inner">
        <p class="recipes-hero__eyebrow">Recettes Mes Meilleurs Menus</p>
        <h1 class="recipes-hero__title">Toutes mes recettes</h1>
      </div>
    </section>

    <!-- Bouton filtres fixe (mobile uniquement) -->
    <button type="button" class="recipes-filter-fab" @click="filtersOpen = !filtersOpen">
      <span>{{ filtersOpen ? '✕ Fermer' : '⚙ Filtres' }}</span>
    </button>

    <!-- Overlay mobile -->
    <div v-if="filtersOpen" class="recipes-filter-overlay" @click="filtersOpen = false"></div>

    <section class="recipes-section">
      <div class="recipes-section__inner">
        <div class="recipes-layout">
          <aside class="recipes-sidebar" :class="{ 'recipes-sidebar--open': filtersOpen }">
            <form class="recipes-filters" method="get" action="/recettes" @submit.prevent="submitFilters">
              <div class="recipes-filters__actions recipes-filters__actions--top">
                <button type="button" class="recipes-reset" :disabled="!hasActiveFilters" @click="resetFilters">
                  Réinitialiser les filtres
                </button>
              </div>

              <div class="recipes-filters__stack">
                <label class="recipes-field">
                  <span class="recipes-field__label">Recherche</span>
                  <input v-model="filters.s" type="search" class="recipes-field__control"
                    placeholder="Je cherche une recette" autocomplete="off" />
                </label>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Types de plat ?</h2>
                    <button type="button" class="recipes-filter-group__clear" @click="clearFilterGroup('dish_type')">
                      Aucun
                    </button>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in dishTypeOptions" :key="option.value" class="recipes-filter-option"
                      :class="{ 'is-active': filters.dish_type.includes(option.value) }">
                      <input type="checkbox" :checked="filters.dish_type.includes(option.value)"
                        @change="toggleMultiFilter('dish_type', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">De saison ?</h2>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in seasonOptions" :key="option.value" class="recipes-filter-option"
                      :class="{ 'is-active': filters.season === option.value }">
                      <input type="radio" name="season-filter" :value="option.value"
                        :checked="filters.season === option.value"
                        @change="selectExclusiveFilter('season', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Végétarien ?</h2>
                    <button type="button" class="recipes-filter-group__clear" @click="clearFilterGroup('vegetarian')">
                      Aucun
                    </button>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in vegetarianOptions" :key="option.value" class="recipes-filter-option"
                      :class="{ 'is-active': filters.vegetarian.includes(option.value) }">
                      <input type="checkbox" :checked="filters.vegetarian.includes(option.value)"
                        @change="toggleMultiFilter('vegetarian', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Sans gluten ?</h2>
                    <button type="button" class="recipes-filter-group__clear" @click="clearFilterGroup('gluten_free')">
                      Aucun
                    </button>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in yesNoOptions" :key="`gluten-${option.value}`" class="recipes-filter-option"
                      :class="{ 'is-active': filters.gluten_free === option.value }">
                      <input type="checkbox" :checked="filters.gluten_free === option.value"
                        @change="toggleExclusiveFilter('gluten_free', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Index glycémique ?</h2>
                    <button type="button" class="recipes-filter-group__clear"
                      @click="clearFilterGroup('glycemic_index')">
                      Aucun
                    </button>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in glycemicIndexOptions" :key="option.value" class="recipes-filter-option"
                      :class="{ 'is-active': filters.glycemic_index.includes(option.value) }">
                      <input type="checkbox" :checked="filters.glycemic_index.includes(option.value)"
                        @change="toggleMultiFilter('glycemic_index', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Bon marché ?</h2>
                    <button type="button" class="recipes-filter-group__clear" @click="clearFilterGroup('budget')">
                      Aucun
                    </button>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in yesNoOptions" :key="`budget-${option.value}`" class="recipes-filter-option"
                      :class="{ 'is-active': filters.budget === option.value }">
                      <input type="checkbox" :checked="filters.budget === option.value"
                        @change="toggleExclusiveFilter('budget', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Temps de préparation + cuisson ?</h2>
                    <button type="button" class="recipes-filter-group__clear" @click="clearFilterGroup('prep_time')">
                      Aucun
                    </button>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in prepTimeOptions" :key="option.value" class="recipes-filter-option"
                      :class="{ 'is-active': isPrepTimeOptionActive(option.value) }">
                      <input type="checkbox" :checked="isPrepTimeOptionActive(option.value)"
                        @change="togglePrepTimeFilter(option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

              </div>

            </form>
          </aside>

          <div class="recipes-content">
            <section class="recipes-toolbar" aria-label="Contrôles de listing">
              <div class="recipes-toolbar__summary">
                <p class="recipes-toolbar__count">
                  {{ totalResults }} recette<span v-if="totalResults !== 1">s</span>
                </p>
                <p class="recipes-toolbar__page">Page {{ currentPage }} sur {{ totalPages }}</p>
              </div>

              <div class="recipes-toolbar__controls">
                <label class="recipes-field recipes-field--compact">
                  <span class="recipes-field__label">Tri</span>
                  <select v-model="filters.sort" class="recipes-field__control" @change="onSortChanged">
                    <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </label>

                <label class="recipes-field recipes-field--compact">
                  <span class="recipes-field__label">Affichage</span>
                  <select v-model="filters.view" class="recipes-field__control" @change="onViewChanged">
                    <option v-for="option in viewOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </label>

                <label class="recipes-field recipes-field--compact">
                  <span class="recipes-field__label">Par page</span>
                  <select v-model="filters.per_page" class="recipes-field__control" @change="onPerPageChanged">
                    <option v-for="option in perPageOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </label>
              </div>
            </section>

            <LoaderView v-if="loading" text="Chargement des recettes..." />

            <div v-else-if="recipes.length === 0" class="recipes-empty">
              Aucune recette ne correspond aux critères sélectionnés, ou l'ingrédient n'est pas de saison actuellement.
            </div>

            <div v-else class="recipes-grid" :class="{ 'recipes-grid--list': filters.view === 'list' }">
              <article v-for="recipe in recipes" :key="recipe.id" class="recipe-card">
                <router-link class="recipe-card__link" :to="{ name: 'recipe', params: { id: recipe.id } }">
                  <div class="recipe-card__media">
                    <img v-if="recipe.photo" :src="mediaSrc(recipe.photo)" :alt="recipe.name"
                      class="recipe-card__image" />
                    <div v-else class="recipe-card__image recipe-card__image--fallback">
                      <span>{{ recipe.name }}</span>
                    </div>
                  </div>

                  <div class="recipe-card__content">
                    <p class="recipe-card__meta">{{ recipe.preparation_time }} min</p>
                    <h2 class="recipe-card__title">{{ recipe.name }}</h2>

                    <!-- Badges info — visibles uniquement en mode liste -->
                    <div class="recipe-card__badges">
                      <span class="recipe-badge" v-if="recipe.difficulty">
                        <font-awesome-icon :icon="['fas', 'signal']" /> {{ recipe.difficulty }}
                      </span>
                      <span class="recipe-badge" v-if="recipe.preparation_time">
                        <font-awesome-icon :icon="['fas', 'clock']" /> {{ recipe.preparation_time }} min
                      </span>
                      <span class="recipe-badge" v-if="recipe.price">
                        <font-awesome-icon :icon="['fas', 'wallet']" /> {{ recipe.price }}
                      </span>
                      <span class="recipe-badge" v-if="recipe.glycemic_index">
                        <font-awesome-icon :icon="['fas', 'chart-line']" /> {{ humanizeGlycemicIndex(recipe.glycemic_index) }}
                      </span>
                      <span class="recipe-badge" v-if="recipe.macro_nutriments">
                        <font-awesome-icon :icon="['fas', 'layer-group']" /> {{ humanizeMacroNutriments(recipe.macro_nutriments) }}
                      </span>
                      <span class="recipe-badge" :class="recipe.is_vegan ? 'recipe-badge--yes' : 'recipe-badge--no'">
                        <font-awesome-icon :icon="['fas', 'seedling']" />
                        <font-awesome-icon :icon="recipe.is_vegan ? ['fas', 'check'] : ['fas', 'xmark']" />
                      </span>
                      <span class="recipe-badge" :class="recipe.is_gluten_free ? 'recipe-badge--yes' : 'recipe-badge--no'">
                        <font-awesome-icon :icon="['fas', 'ban']" />
                        <font-awesome-icon :icon="recipe.is_gluten_free ? ['fas', 'check'] : ['fas', 'xmark']" />
                      </span>
                    </div>

                    <span class="recipe-card__cta">Voir la recette</span>
                  </div>
                </router-link>
              </article>
            </div>

            <nav v-if="!loading && totalPages > 1" class="recipes-pagination" aria-label="Pagination des recettes">
              <button type="button" class="recipes-pagination__button" :disabled="currentPage <= 1"
                @click="goToPage(currentPage - 1)">
                Précédent
              </button>

              <button v-for="page in visiblePages" :key="page" type="button" class="recipes-pagination__button"
                :class="{ 'recipes-pagination__button--active': page === currentPage }" @click="goToPage(page)">
                {{ page }}
              </button>

              <button type="button" class="recipes-pagination__button" :disabled="currentPage >= totalPages"
                @click="goToPage(currentPage + 1)">
                Suivant
              </button>
            </nav>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { api } from "@/api";
import LoaderView from "@/components/LoaderView.vue";

const DEFAULT_FILTERS = {
  s: "",
  dish_type: [],
  vegetarian: [],
  gluten_free: "",
  glycemic_index: [],
  budget: "",
  prep_time: [],
  season: "actuelle",
  sort: "newest",
  view: "list",
  per_page: "20",
  page: 1,
};

const MULTI_FILTER_KEYS = ["dish_type", "vegetarian", "glycemic_index", "prep_time"];

export default {
  name: "RecipesListView",
  components: {
    LoaderView,
  },
  data() {
    return {
      loading: true,
      filtersOpen: false,
      recipes: [],
      totalResults: 0,
      currentPage: 1,
      totalPages: 1,
      filters: this.getDefaultFilters(),
      dishTypeOptions: [
        { value: "entree", label: "Entrée" },
        { value: "plat", label: "Plat" },
        { value: "dessert", label: "Dessert" },
      ],
      vegetarianOptions: [
        { value: "vegetarien", label: "Végétarien" },
        { value: "viande", label: "Contient de la viande" },
        { value: "poisson", label: "Contient du poisson" },
      ],
      yesNoOptions: [
        { value: "oui", label: "Oui" },
        { value: "non", label: "Non" },
      ],
      glycemicIndexOptions: [
        { value: "faible", label: "Faible" },
        { value: "moyen", label: "Moyen" },
        { value: "haut", label: "Haut" },
      ],
      prepTimeOptions: [
        { value: "any", label: "Peu importe" },
        { value: "lt30", label: "< 30 min" },
        { value: "lt40", label: "< 40 min" },
        { value: "gte60", label: "60 min ou plus" },
      ],
      seasonOptions: [
        { value: "actuelle", label: "Recettes de saison" },
        { value: "all", label: "Toutes les recettes" },
      ],
      sortOptions: [
        { value: "newest", label: "Plus récentes" },
        { value: "name_asc", label: "Nom A -> Z" },
        { value: "name_desc", label: "Nom Z -> A" },
        { value: "prep_time_asc", label: "Préparation croissante" },
        { value: "prep_time_desc", label: "Préparation décroissante" },
      ],
      viewOptions: [
        { value: "grid", label: "Grille" },
        { value: "list", label: "Liste" },
      ],
      perPageOptions: [
        { value: "20", label: "20" },
        { value: "40", label: "40" },
        { value: "80", label: "80" },
      ],
    };
  },
  computed: {
    hasActiveFilters() {
      const { query } = this.$route;

      return ["s", "dish_type", "vegetarian", "gluten_free", "glycemic_index", "budget", "prep_time", "season"]
        .some((key) => {
          const value = query[key];
          if (Array.isArray(value)) {
            return value.some((item) => String(item || "").trim());
          }

          return !!String(value || "").trim();
        });
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);

      for (let page = start; page <= end; page += 1) {
        pages.push(page);
      }

      return pages;
    },
  },
  created() {
    const validSeasons = ["actuelle", "all"];
    if (!validSeasons.includes(this.$route.query.season)) {
      this.$router.replace({
        name: "recipes",
        query: { ...this.$route.query, season: "actuelle" },
      }).catch(() => {});
    }
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.applyRouteQueryToFilters();
        this.updateHead();
        this.loadRecipes();
      },
    },
  },
  methods: {
    humanizeGlycemicIndex(g) {
      if (g === "low") return "IG faible";
      if (g === "medium") return "IG moyen";
      if (g === "high") return "IG élevé";
      return g || "";
    },
    humanizeMacroNutriments(value) {
      if (!value) return "";
      const map = { P: "Protéines", L: "Lipides", G: "Glucides" };
      return value.split("-").map(l => map[l] || l).join(" - ");
    },
    getDefaultFilters() {
      return {
        ...DEFAULT_FILTERS,
        dish_type: [],
        vegetarian: [],
        glycemic_index: [],
        prep_time: [],
      };
    },
    loadRecipes() {
      this.loading = true;

      api.get("/recipes/", {
        params: this.buildApiQueryFromFilters(),
      })
        .then((response) => {
          const payload = response.data || {};
          this.recipes = Array.isArray(payload.results) ? payload.results : [];
          this.totalResults = Number(payload.total_results) || 0;
          this.currentPage = Number(payload.current_page) || 1;
          this.totalPages = Number(payload.total_pages) || 1;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyRouteQueryToFilters() {
      const routeQuery = this.$route.query;

      this.filters = {
        s: this.normalizeSearchValue(routeQuery.s),
        dish_type: this.normalizeMultiChoiceValue(
          routeQuery.dish_type,
          this.dishTypeOptions.map((option) => option.value),
        ),
        vegetarian: this.normalizeMultiChoiceValue(
          routeQuery.vegetarian,
          this.vegetarianOptions.map((option) => option.value),
        ),
        gluten_free: this.normalizeChoiceValue(
          routeQuery.gluten_free,
          this.yesNoOptions.map((option) => option.value),
        ),
        glycemic_index: this.normalizeMultiChoiceValue(
          routeQuery.glycemic_index,
          this.glycemicIndexOptions.map((option) => option.value),
        ),
        budget: this.normalizeChoiceValue(
          routeQuery.budget,
          this.yesNoOptions.map((option) => option.value),
        ),
        prep_time: this.normalizeMultiChoiceValue(
          routeQuery.prep_time,
          this.getPrepTimeFilterValues(),
        ),
        season: this.normalizeChoiceValue(
          routeQuery.season,
          this.seasonOptions.map((option) => option.value),
          "actuelle",
        ),
        sort: this.normalizeChoiceValue(
          routeQuery.sort,
          this.sortOptions.map((option) => option.value),
          DEFAULT_FILTERS.sort,
        ),
        view: this.normalizeChoiceValue(
          routeQuery.view,
          this.viewOptions.map((option) => option.value),
          DEFAULT_FILTERS.view,
        ),
        per_page: this.normalizeChoiceValue(
          routeQuery.per_page,
          this.perPageOptions.map((option) => option.value),
          DEFAULT_FILTERS.per_page,
        ),
        page: this.normalizePageValue(routeQuery.page),
      };
    },
    getSingleQueryValue(value) {
      if (Array.isArray(value)) {
        return value[0] || "";
      }

      return typeof value === "string" ? value : "";
    },
    getQueryValues(value) {
      const rawValues = Array.isArray(value) ? value : [value];

      return rawValues
        .flatMap((item) => String(item || "").split(","))
        .map((item) => item.trim())
        .filter(Boolean);
    },
    normalizeSearchValue(value) {
      return this.getSingleQueryValue(value).trim();
    },
    normalizeChoiceValue(value, allowedValues, fallback = "") {
      const normalizedValue = this.getSingleQueryValue(value);

      if (allowedValues.includes(normalizedValue)) {
        return normalizedValue;
      }

      return fallback;
    },
    normalizeMultiChoiceValue(value, allowedValues) {
      return [...new Set(this.getQueryValues(value).filter((item) => allowedValues.includes(item)))];
    },
    getPrepTimeFilterValues() {
      return this.prepTimeOptions
        .filter((option) => option.value !== "any")
        .map((option) => option.value);
    },
    normalizePageValue(value) {
      const page = Number.parseInt(this.getSingleQueryValue(value), 10);
      if (Number.isInteger(page) && page > 0) {
        return page;
      }

      return 1;
    },
    buildJoinedQueryValue(values) {
      return values.join(",");
    },
    buildApiQueryFromFilters() {
      const query = {};

      if (this.filters.s) {
        query.s = this.filters.s;
      }
      if (this.filters.dish_type.length) {
        query.dish_type = this.buildJoinedQueryValue(this.filters.dish_type);
      }
      if (this.filters.vegetarian.length) {
        query.vegetarian = this.buildJoinedQueryValue(this.filters.vegetarian);
      }
      if (this.filters.gluten_free) {
        query.gluten_free = this.filters.gluten_free;
      }
      if (this.filters.glycemic_index.length) {
        query.glycemic_index = this.buildJoinedQueryValue(this.filters.glycemic_index);
      }
      if (this.filters.budget) {
        query.budget = this.filters.budget;
      }
      if (this.filters.prep_time.length) {
        query.prep_time = this.buildJoinedQueryValue(this.filters.prep_time);
      }
      if (this.filters.season) {
        query.season = this.filters.season;
      }

      query.sort = this.filters.sort;
      query.per_page = this.filters.per_page;
      query.page = this.filters.page;

      return query;
    },
    buildRouteQueryFromFilters() {
      const query = {};

      if (this.filters.s) {
        query.s = this.filters.s;
      }
      if (this.filters.dish_type.length) {
        query.dish_type = this.buildJoinedQueryValue(this.filters.dish_type);
      }
      if (this.filters.vegetarian.length) {
        query.vegetarian = this.buildJoinedQueryValue(this.filters.vegetarian);
      }
      if (this.filters.gluten_free) {
        query.gluten_free = this.filters.gluten_free;
      }
      if (this.filters.glycemic_index.length) {
        query.glycemic_index = this.buildJoinedQueryValue(this.filters.glycemic_index);
      }
      if (this.filters.budget) {
        query.budget = this.filters.budget;
      }
      if (this.filters.prep_time.length) {
        query.prep_time = this.buildJoinedQueryValue(this.filters.prep_time);
      }
      if (this.filters.season) {
        query.season = this.filters.season;
      }
      if (this.filters.sort !== DEFAULT_FILTERS.sort) {
        query.sort = this.filters.sort;
      }
      if (this.filters.view !== DEFAULT_FILTERS.view) {
        query.view = this.filters.view;
      }
      if (this.filters.per_page !== DEFAULT_FILTERS.per_page) {
        query.per_page = this.filters.per_page;
      }
      if (this.filters.page > 1) {
        query.page = String(this.filters.page);
      }

      return query;
    },
    buildNormalizedRouteQuery(routeQuery) {
      const normalizedFilters = {
        s: this.normalizeSearchValue(routeQuery.s),
        dish_type: this.normalizeMultiChoiceValue(
          routeQuery.dish_type,
          this.dishTypeOptions.map((option) => option.value),
        ),
        vegetarian: this.normalizeMultiChoiceValue(
          routeQuery.vegetarian,
          this.vegetarianOptions.map((option) => option.value),
        ),
        gluten_free: this.normalizeChoiceValue(
          routeQuery.gluten_free,
          this.yesNoOptions.map((option) => option.value),
        ),
        glycemic_index: this.normalizeMultiChoiceValue(
          routeQuery.glycemic_index,
          this.glycemicIndexOptions.map((option) => option.value),
        ),
        budget: this.normalizeChoiceValue(
          routeQuery.budget,
          this.yesNoOptions.map((option) => option.value),
        ),
        prep_time: this.normalizeMultiChoiceValue(
          routeQuery.prep_time,
          this.getPrepTimeFilterValues(),
        ),
        season: this.normalizeChoiceValue(
          routeQuery.season,
          this.seasonOptions.map((option) => option.value),
          "actuelle",
        ),
        sort: this.normalizeChoiceValue(
          routeQuery.sort,
          this.sortOptions.map((option) => option.value),
          DEFAULT_FILTERS.sort,
        ),
        view: this.normalizeChoiceValue(
          routeQuery.view,
          this.viewOptions.map((option) => option.value),
          DEFAULT_FILTERS.view,
        ),
        per_page: this.normalizeChoiceValue(
          routeQuery.per_page,
          this.perPageOptions.map((option) => option.value),
          DEFAULT_FILTERS.per_page,
        ),
        page: this.normalizePageValue(routeQuery.page),
      };

      const currentFilters = this.filters;
      this.filters = normalizedFilters;
      const normalizedQuery = this.buildRouteQueryFromFilters();
      this.filters = currentFilters;

      return normalizedQuery;
    },
    submitFilters() {
      this.filters.s = this.filters.s.trim();
      this.filters.page = 1;
      this.pushRouteQuery();
    },
    applyImmediateFilters() {
      this.filters.page = 1;
      this.replaceRouteQuery();
    },
    isPrepTimeOptionActive(optionValue) {
      if (optionValue === "any") {
        return this.filters.prep_time.length === 0;
      }

      return this.filters.prep_time.includes(optionValue);
    },
    togglePrepTimeFilter(optionValue) {
      if (optionValue === "any") {
        this.clearFilterGroup("prep_time");
        return;
      }

      this.toggleMultiFilter("prep_time", optionValue);
    },
    toggleMultiFilter(groupName, optionValue) {
      const nextValues = this.filters[groupName].includes(optionValue)
        ? this.filters[groupName].filter((value) => value !== optionValue)
        : [...this.filters[groupName], optionValue];

      this.filters = {
        ...this.filters,
        [groupName]: nextValues,
      };
      this.applyImmediateFilters();
    },
    toggleExclusiveFilter(groupName, optionValue) {
      this.filters = {
        ...this.filters,
        [groupName]: this.filters[groupName] === optionValue ? "" : optionValue,
      };
      this.applyImmediateFilters();
    },

    selectExclusiveFilter(groupName, optionValue) {
      // Comportement bouton radio : cliquer sur l'option deja active ne fait
      // rien, cliquer sur l'autre bascule dessus. Contrairement a
      // toggleExclusiveFilter, on ne peut jamais se retrouver sans choix.
      if (this.filters[groupName] === optionValue) {
        return;
      }

      this.filters = {
        ...this.filters,
        [groupName]: optionValue,
      };
      this.applyImmediateFilters();
    },
    clearFilterGroup(groupName) {
      this.filters = {
        ...this.filters,
        [groupName]: MULTI_FILTER_KEYS.includes(groupName) ? [] : "",
      };
      this.applyImmediateFilters();
    },
    onSortChanged() {
      this.filters.page = 1;
      this.replaceRouteQuery();
    },
    onViewChanged() {
      this.filters.page = 1;
      this.replaceRouteQuery();
    },
    onPerPageChanged() {
      this.filters.page = 1;
      this.replaceRouteQuery();
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }

      this.filters.page = page;
      this.$router.push({
        name: "recipes",
        query: this.buildRouteQueryFromFilters(),
      });
    },
    pushRouteQuery() {
      const nextQuery = this.buildRouteQueryFromFilters();
      const currentQuery = this.buildNormalizedRouteQuery(this.$route.query);

      if (JSON.stringify(nextQuery) === JSON.stringify(currentQuery)) {
        return;
      }

      this.$router.push({
        name: "recipes",
        query: nextQuery,
      });
    },
    replaceRouteQuery() {
      const nextQuery = this.buildRouteQueryFromFilters();
      const currentQuery = this.buildNormalizedRouteQuery(this.$route.query);

      if (JSON.stringify(nextQuery) === JSON.stringify(currentQuery)) {
        return;
      }

      this.$router.replace({
        name: "recipes",
        query: nextQuery,
      }).catch(() => {});
    },
    resetFilters() {
      this.filters = this.getDefaultFilters();
      this.$router.replace({
        name: "recipes",
        query: {},
      }).catch(() => {});
    },
    updateHead() {
      const titleParts = ["Recettes"];
      const activeDishTypeLabels = this.dishTypeOptions
        .filter((option) => this.filters.dish_type.includes(option.value))
        .map((option) => option.label);

      if (activeDishTypeLabels.length) {
        titleParts.unshift(activeDishTypeLabels.join(", "));
      }

      if (this.filters.s) {
        titleParts.unshift(this.filters.s);
      }

      document.title = `${titleParts.join(" | ")} | Mes Meilleurs Menus`;

      const descriptionParts = ["Découvrez les recettes Mes Meilleurs Menus"];

      if (this.filters.s) {
        descriptionParts.push(`autour de "${this.filters.s}"`);
      }

      if (activeDishTypeLabels.length) {
        descriptionParts.push(`filtrées par type de plat : ${activeDishTypeLabels.join(", ").toLowerCase()}`);
      }

      const description =
        `${descriptionParts.join(" ")}. Filtrez-les par profil alimentaire, sans gluten, index glycémique, budget, saison et temps de préparation.`;
      this.setMetaTag("description", description);
      this.setCanonicalLink();
    },
    setMetaTag(name, content) {
      let tag = document.head.querySelector(`meta[name="${name}"]`);

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    },
    setCanonicalLink() {
      let tag = document.head.querySelector('link[rel="canonical"]');

      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "canonical");
        document.head.appendChild(tag);
      }

      tag.setAttribute("href", `${window.location.origin}${this.$route.fullPath}`);
    },
    mediaSrc(uri) {
      if (!uri) return null;
      if (uri.startsWith("http")) return uri;
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },
    shortRecipe(recipe) {
      const text = (recipe || "").replace(/\s+/g, " ").trim();

      if (!text) {
        return "Découvrez cette recette Mes Meilleurs Menus et consultez sa préparation détaillée.";
      }

      if (text.length <= 140) {
        return text;
      }

      return `${text.slice(0, 137).trim()}...`;
    },
  },
};
</script>

<style scoped>
.recipes-list-view {
  padding-top: 110px;
  padding-bottom: 48px;
}

.recipes-hero {
  padding: 100px 20px 24px;
}

.recipes-hero__inner {
  max-width: 1320px;
  margin: 0 auto;
}

.recipes-hero__eyebrow {
  margin: 0 0 8px;
  color: #e57b57;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.recipes-hero__title {
  margin: 0;
  color: #004c40;
  font-family: "Amanise";
  font-size: clamp(36px, 4vw, 60px);
  line-height: 1.05;
}

.recipes-hero__subtitle {
  max-width: 720px;
  margin: 16px 0 0;
  color: #35524a;
  font-size: 18px;
  line-height: 1.6;
}

.recipes-section {
  padding: 0 20px;
}

.recipes-section__inner {
  max-width: 1320px;
  margin: 0 auto;
}

.recipes-layout {
  display: grid;
  grid-template-columns: minmax(280px, 320px) minmax(0, 1fr);
  gap: 32px;
  align-items: start;
}

.recipes-sidebar,
.recipes-content {
  min-width: 0;
}

.recipes-filters {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  border-radius: 18px;
  background: #f5f1e8;
  position: sticky;
  top: 126px;
}

.recipes-filters__stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.recipes-filter-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 18px;
  border-top: 1px solid rgba(0, 76, 64, 0.12);
}

.recipes-filter-group:first-of-type {
  padding-top: 0;
  border-top: none;
}

.recipes-filter-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.recipes-filter-group__title {
  margin: 0;
  color: #004c40;
  font-size: 15px;
  font-weight: 700;
}

.recipes-filter-group__clear {
  border: none;
  background: none;
  color: #6a7a74;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}

.recipes-filter-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.recipes-filter-option {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 46px;
  padding: 10px 12px;
  border: 1px solid #d8ddd2;
  border-radius: 12px;
  background: #fff;
  color: #35524a;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.recipes-filter-option input {
  width: 16px;
  height: 16px;
  accent-color: #004c40;
  flex: 0 0 auto;
}

.recipes-filter-option span {
  font-size: 14px;
  font-weight: 500;
}

.recipes-filter-option.is-active {
  border-color: #004c40;
  background: rgba(0, 76, 64, 0.06);
  box-shadow: inset 0 0 0 1px rgba(0, 76, 64, 0.08);
}

.recipes-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recipes-field--compact {
  min-width: 160px;
}

.recipes-field__label {
  color: #004c40;
  font-size: 14px;
  font-weight: 700;
}

.recipes-field__control {
  min-height: 46px;
  padding: 10px 12px;
  border: 1px solid #d8ddd2;
  border-radius: 12px;
  background: #fff;
  color: #35524a;
}

.recipes-filters__actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
}

.recipes-filters__actions--top {
  margin-top: 0;
}

.recipes-submit,
.recipes-reset {
  width: 100%;
  min-height: 46px;
  padding: 0 18px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
}

.recipes-submit {
  border: 1px solid #004c40;
  background: #004c40;
  color: #fff;
}

.recipes-reset {
  border: 1px solid #004c40;
  background: transparent;
  color: #004c40;
}

.recipes-reset:disabled {
  opacity: 0.45;
  cursor: default;
}

.recipes-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 24px;
}

.recipes-toolbar__summary {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.recipes-toolbar__count,
.recipes-toolbar__page {
  margin: 0;
  color: #35524a;
}

.recipes-toolbar__count {
  color: #004c40;
  font-size: 18px;
  font-weight: 700;
}

.recipes-toolbar__controls {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: end;
}

.recipes-empty {
  padding: 48px 24px;
  border-radius: 18px;
  background: #f5f1e8;
  color: #35524a;
  text-align: center;
}

.recipes-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.recipes-grid--list {
  grid-template-columns: 1fr;
}

.recipes-grid--list .recipe-card__link {
  flex-direction: row;
}

.recipes-grid--list .recipe-card__media {
  flex: 0 0 220px;
  aspect-ratio: auto;
  height: 180px;
  min-height: 180px;
  overflow: hidden;
}

.recipes-grid--list .recipe-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.recipes-grid--list .recipe-card__title {
  font-size: 17px;
}

.recipes-grid--list .recipe-card__badges {
  display: flex;
}

.recipe-card__badges {
  display: none;
  flex-wrap: wrap;
  gap: 6px;
  margin: 10px 0;
}

.recipe-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  background: #f0f4f0;
  color: #35524a;
  font-size: 12px;
  font-weight: 600;
}

.recipe-badge--yes {
  background: #e6f4ec;
  color: #1e7d3f;
}

.recipe-badge--no {
  background: #f4f4f4;
  color: #555;
}

.recipe-card {
  min-width: 0;
}

.recipe-card__link {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(0, 76, 64, 0.08);
  color: inherit;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.recipe-card__link:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 36px rgba(0, 76, 64, 0.14);
}

.recipe-card__media {
  position: relative;
  aspect-ratio: 4 / 3;
  background: #e8efe8;
}

.recipe-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  height: 180px;
}

.recipe-card__image--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, rgba(181, 208, 136, 0.9), rgba(0, 76, 64, 0.9));
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
}

.recipe-card__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 14px;
}

.recipe-card__meta {
  margin: 0 0 6px;
  color: #e57b57;
  font-size: 12px;
  font-weight: 700;
}

.recipe-card__title {
  margin: 0 0 8px;
  color: #004c40;
  font-size: 13px;
  line-height: 1.25;
}

.recipe-card__excerpt {
  margin: 0;
  color: #4a5f58;
  font-size: 15px;
  line-height: 1.6;
}

.recipe-card__cta {
  margin-top: auto;
  padding-top: 10px;
  color: #004c40;
  font-size: 12px;
  font-weight: 700;
}

/* ── Bouton filtres fixe (mobile) ── */
.recipes-filter-fab {
  display: none;
}

.recipes-filter-overlay {
  display: none;
}

@media only screen and (max-width: 1023px) {
  .recipes-filter-fab {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    bottom: 24px;
    right: 20px;
    z-index: 200;
    padding: 12px 22px;
    border: none;
    border-radius: 50px;
    background: #004c40;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    box-shadow: 0 4px 16px rgba(0, 76, 64, 0.35);
    cursor: pointer;
  }

  .recipes-filter-overlay {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 149;
    background: rgba(0, 0, 0, 0.4);
  }

  .recipes-sidebar {
    display: none;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 150;
    max-height: 80vh;
    overflow-y: auto;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -4px 24px rgba(0, 76, 64, 0.2);
  }

  .recipes-sidebar--open {
    display: block;
  }
}

.recipes-pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 32px;
}

.recipes-pagination__button {
  min-width: 44px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #d8ddd2;
  border-radius: 12px;
  background: #fff;
  color: #35524a;
  cursor: pointer;
}

.recipes-pagination__button--active {
  border-color: #004c40;
  background: #004c40;
  color: #fff;
}

.recipes-pagination__button:disabled {
  opacity: 0.45;
  cursor: default;
}

@media only screen and (max-width: 1199px) {
  .recipes-grid:not(.recipes-grid--list) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 1023px) {
  .recipes-layout {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .recipes-filters {
    position: static;
  }

  .recipes-filter-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .recipes-grid:not(.recipes-grid--list) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 899px) {
  .recipes-list-view {
    padding-top: 90px;
  }

  .recipes-grid:not(.recipes-grid--list) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .recipes-grid--list {
    grid-template-columns: 1fr;
  }

  .recipes-grid--list .recipe-card__link {
    flex-direction: column;
  }

  .recipes-grid--list .recipe-card__media {
    flex: none;
    height: 180px;
    min-height: 0;
    aspect-ratio: auto;
  }
}

@media only screen and (max-width: 599px) {
  .recipes-hero {
    padding-left: 16px;
    padding-right: 16px;
  }

  .recipes-section {
    padding-left: 16px;
    padding-right: 16px;
  }

  .recipes-filters {
    padding: 18px;
  }

  .recipes-filter-options {
    grid-template-columns: 1fr;
  }

  .recipes-toolbar {
    align-items: stretch;
  }

  .recipes-toolbar__controls {
    flex-direction: column;
    align-items: stretch;
  }

  .recipes-field--compact {
    min-width: 0;
  }

  .recipes-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .recipe-card__content {
    padding: 18px;
  }

  .recipe-card__title {
    font-size: 20px;
  }
}
</style>